import { PropsWithChildren, useEffect, useState } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import {
    WCL_GRID_MAX_WIDTH,
    WCL_GRID_PADDING_LG,
    WCL_GRID_PADDING_SM
} from "@/libraries/design/foundations/grid/src/base";

import { Tab } from "./tab";
import { TabList } from "./tab-list";
import { TabPanel } from "./tab-panel";

export type TabData = {
    id?: string;
    title: string;
    iconUrl?: string;
    tabComponent: React.ReactElement;
};

export type TabSize = "small" | "large";

export type TabsSectionProps = PropsWithChildren<{
    readonly tabsData: TabData[];
    tabSize?: TabSize;
}>;

const getResolvedTabId = (tab: TabData, index: number): string =>
    tab.id ?? `tab-${index + 1}`;

const getTabIdFromHash = (tabsData: TabData[]): string | undefined => {
    if (typeof window === "undefined") {
        return undefined;
    }
    const hash = window.location.hash.slice(1);
    return tabsData.find((tab, index) => getResolvedTabId(tab, index) === hash)
        ? hash
        : undefined;
};

const sectionStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.300")
});

const tabListContainerStyles = css({
    [media.above.md]: {
        // Slots can be any element and placed dynamically via Uniform
        // Due to the way Uniform slots in content (with extra script elements) we have to be a bit more mathematical
        // to get the intended grid alignment
        "gridTemplateColumns": "repeat(12, 1fr)",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "> *": {
            gridColumn: "3 / span 8"
        }
    }
});

const tabListInnerContainerStyles = css({
    marginInline: "auto",
    maxWidth: "100%"
});

const baseGridStyles = css({
    display: "grid",
    boxSizing: "border-box",
    gap: "var(--cl-gap)",
    width: "100%",
    ["--cl-gap"]: "24px",
    ["--width-without-gaps"]: "calc(100% - var(--cl-gap) * 11)", // width of only the 12 columns, subtracting the 11 gaps in 12 column layout
    // Prevent child content from expanding grid columns (minWidth: 0 allows shrinking below content size)
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["> *"]: {
        minWidth: 0
    },
    marginInline: "auto",
    maxWidth: "100%",
    paddingInlineStart: `${WCL_GRID_PADDING_SM}px`,
    paddingInlineEnd: `${WCL_GRID_PADDING_SM}px`,
    [media.only.sm]: {
        paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
        paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`
    },
    [media.above.md]: {
        paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
        paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
        maxWidth: `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`,
        ["--cl-gap"]: "32px"
    }
});

export const TabsSection = ({
    tabsData,
    tabSize = "large"
}: TabsSectionProps) => {
    const [activeTabId, setActiveTabId] = useState(
        () =>
            getTabIdFromHash(tabsData) ??
            (tabsData[0] ? getResolvedTabId(tabsData[0], 0) : undefined)
    );

    const handleTabSelect = (tabId: string) => {
        setActiveTabId(tabId);
        // Update URL hash without scrolling
        window.history.replaceState(null, "", `#${tabId}`);
    };

    // Listen for browser back/forward navigation
    useEffect(() => {
        const handleHashChange = () => {
            const tabId = getTabIdFromHash(tabsData);
            if (tabId) {
                setActiveTabId(tabId);
            }
        };

        window.addEventListener("hashchange", handleHashChange);
        return () => {
            window.removeEventListener("hashchange", handleHashChange);
        };
    }, [tabsData]);

    return (
        <section id="tabs" css={sectionStyles}>
            <div css={[baseGridStyles, tabListContainerStyles]}>
                <div css={tabListInnerContainerStyles}>
                    <TabList activeTabId={activeTabId}>
                        {tabsData.map((tab, index) => {
                            const resolvedId = getResolvedTabId(tab, index);
                            return (
                                <Tab
                                    key={resolvedId}
                                    tabId={resolvedId}
                                    index={index}
                                    title={tab.title}
                                    isActive={resolvedId === activeTabId}
                                    onTabSelect={handleTabSelect}
                                    tabSize={tabSize}
                                />
                            );
                        })}
                    </TabList>
                </div>
            </div>
            <div className="tab-panels" css={[baseGridStyles]}>
                {tabsData.map((tab, index) => {
                    const resolvedId = getResolvedTabId(tab, index);
                    return (
                        <TabPanel
                            id={resolvedId}
                            key={resolvedId}
                            isActive={resolvedId === activeTabId}
                        >
                            {tab.tabComponent}
                        </TabPanel>
                    );
                })}
            </div>
        </section>
    );
};
